import { MatchStatusEnum, MatchVm, TeamVm } from '../../utils/api';

/*
 * Club-first reading of a match: the portal always looks at a result from "our" side,
 * where "our" is the selected club (optionally narrowed to one of its teams).
 */

export type MatchResult = 'W' | 'D' | 'L';

export interface ClubMatchView {
    match: MatchVm;
    isHome: boolean;
    opponentName: string;
    goalsFor?: number;
    goalsAgainst?: number;
    result?: MatchResult;
    isFinished: boolean;
}

export interface ClubSummary {
    played: number;
    wins: number;
    draws: number;
    losses: number;
    goalsFor: number;
    goalsAgainst: number;
    /** Newest first, capped by the caller's slice. */
    form: MatchResult[];
}

export const emptyClubSummary: ClubSummary = {
    played: 0,
    wins: 0,
    draws: 0,
    losses: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    form: [],
};

export const ourTeamIds = (teams: TeamVm[], selectedTeamId?: number): number[] =>
    selectedTeamId != null
        ? [selectedTeamId]
        : teams.map((team: TeamVm) => team.id).filter((id): id is number => id != null);

export const isFinishedMatch = (match: MatchVm): boolean =>
    match.statusId === MatchStatusEnum.Finished;

export const toClubMatchView = (match: MatchVm, teamIds: number[]): ClubMatchView | undefined => {
    const isHome = teamIds.includes(match.homeTeamId);
    const isAway = teamIds.includes(match.awayTeamId);
    if (!isHome && !isAway) return undefined;

    const goalsFor = isHome ? match.homeTeamGoalsTotal : match.awayTeamGoalsTotal;
    const goalsAgainst = isHome ? match.awayTeamGoalsTotal : match.homeTeamGoalsTotal;
    const finished = isFinishedMatch(match);

    let result: MatchResult | undefined;
    if (finished && goalsFor != null && goalsAgainst != null) {
        result = goalsFor > goalsAgainst ? 'W' : goalsFor < goalsAgainst ? 'L' : 'D';
    }

    return {
        match,
        isHome,
        opponentName:
            (isHome ? match.awayTeamDisplayName : match.homeTeamDisplayName) || 'Unknown opponent',
        goalsFor,
        goalsAgainst,
        result,
        isFinished: finished,
    };
};

/** Matches must arrive newest first; only finished matches count towards the record. */
export const summarizeClubMatches = (
    matches: MatchVm[],
    teamIds: number[],
    formLength = 5
): ClubSummary => {
    const summary: ClubSummary = { ...emptyClubSummary, form: [] };

    matches.forEach((match: MatchVm) => {
        const view = toClubMatchView(match, teamIds);
        if (!view || !view.result) return;

        summary.played += 1;
        summary.goalsFor += view.goalsFor || 0;
        summary.goalsAgainst += view.goalsAgainst || 0;
        if (view.result === 'W') summary.wins += 1;
        else if (view.result === 'D') summary.draws += 1;
        else summary.losses += 1;

        if (summary.form.length < formLength) summary.form.push(view.result);
    });

    return summary;
};

export const goalDifference = (summary: ClubSummary): number =>
    summary.goalsFor - summary.goalsAgainst;

export const perGame = (total: number, played: number): string =>
    played > 0 ? (total / played).toFixed(1) : '-';
